/**
 * The lot (AI 10) and expiration (AI 17) of the barcode that opened an item screen, carried from
 * the Scanner through Scanned Options to Receive / Stock so those screens can send them.
 *
 * Why: a serialized unit whose EPCIS file named no lot or expiration was stocked into an undated,
 * lot-less balance — invisible to FEFO and left out of RedSail's catalog, which requires both for
 * Rx. The server records the scanned values on such a unit at intake (fill-only: a lot already on
 * file is never replaced), so all these screens have to do is pass along what was scanned.
 */

type ScannedLot = { lot: string | null; expiration: string | null };

/** `&lot=…&expiration=…` for the scanned values that exist (empty when neither does). */
export const scannedLotSearch = ({ lot, expiration }: Partial<ScannedLot>): string =>
  `${lot ? `&lot=${encodeURIComponent(lot)}` : ''}${
    expiration ? `&expiration=${encodeURIComponent(expiration)}` : ''
  }`;

/** The scanned values a screen was opened with. */
export const readScannedLot = (searchParams: URLSearchParams): ScannedLot => ({
  lot: searchParams.get('lot') || null,
  expiration: searchParams.get('expiration') || null
});

/**
 * Mutation variables for the scanned values. The lot is upper-cased as the pick screen sends it.
 * AI 17 is a calendar date with no time: the MDate scalar runs input through
 * `new Date(v).toLocaleDateString()`, which shifts a midnight-UTC date by a day on a server west
 * of UTC, so it is sent as noon UTC (the same convention as SalesOrderPickScan).
 */
export const scannedLotVariables = ({
  lot,
  expiration
}: Partial<ScannedLot>): { lot?: string; expiration?: number } => {
  const m = expiration ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(expiration) : null;
  return {
    ...(lot ? { lot: lot.toUpperCase() } : {}),
    ...(m ? { expiration: Date.UTC(+m[1], +m[2] - 1, +m[3], 12, 0, 0) } : {})
  };
};
